import _Model from "./_Model";
import ModuleBlock from "./ModuleBlock";
import Device from "./Device";
import RangeInfo from "./RangeInfo";
import Validator from "./Validator";
import IOIsland from "./IOIsland";
import PowerSupplyBus from "./PowerSupplyBus";
import ConfigurationAccessories from "./ConfigurationAccessories";
import Rack from "./Rack";
import { M580_SAFETY_COPROCESSOR, PRECOMM_STATUS, configurationModuleType, m172_battery_backup, pac_optical_fiber_converter, possible_starter_selections, rangeIOModules, tm172sig_type } from "../constants";
import PowerSupplyManagement from "./PowerSupplyManagement";
import PacConsumptionBlock from "./PacConsumptionBlock";
import SoftwareLicence from "./SoftwareLicence";
import ServicePlanItem from "./ServicePlanItem";
import EdgeModuleBlock from "./EdgeModuleBlock";
import TerminalBlockPreferences from "./TerminalBlockPreferences";
import I10n from "../features/i10n";
import OutdatedLicense from "./OutdatedLicense";
import BOMMap from "./BOMMap";
import LicenseItem from "./LicenseItem";
import RedBox from "./RedBox";
import SoftwareInfo from "./SoftwareInfo";
import { buildLicenseGroups } from "../utils/software-licenses";
import SoftwareLicenseSelection from "./SoftwareLicenseSelection";

export default class Configuration extends _Model {
  constructor(json) {
    super(json);
    this.id = Configuration.getOrDefault(json, 'id', '');
    this.name = Configuration.getOrDefault(json, 'name', '');
    this.status = Configuration.getOrDefault(json, 'status', null);
    this.headModule = Configuration.getMappedOrDefault(json, 'headModule', null, data => new Device(data || {}));
    this.rack = Configuration.getMappedOrDefaultWithUndefined(json, 'rack', null, data => new Rack(data));
    this.rangeInfo = Configuration.getMappedOrDefault(json, 'rangeInfo', null, data => new RangeInfo(data || {}));
    this.ioModules = Configuration.getMappedOrDefault(json, 'ioModules', [], data => (data || []).map(d => new Device(d || {})));
    this.comModules = Configuration.getMappedOrDefault(json, 'comModules', [], data => (data || []).map(d => new Device(d || {})));
    this.cartridges = Configuration.getMappedOrDefault(json, 'cartridges', [], data => (data || []).map(d => new Device(d || {})));
    this.ioIslands = Configuration.getMappedOrDefault(json, 'ioIslands', [], data => (data || []).map(i => new IOIsland(i)));
    this.localRacks = Configuration.getMappedOrDefault(json, 'localRacks', [], data => (data || []).map(c => new Configuration(c)));
    this.redboxes = Configuration.getMappedOrDefault(json, 'redboxes', [], data => (data || []).map(r => new RedBox(r)));
    this.ioModuleBlocks = Configuration.getMappedOrDefault(json, 'ioModuleBlocks', [], data => (data || []).map(b => this._createNewModuleBlock(b || {})))
    this.powerSupplyBuses = Configuration.getMappedOrDefault(json, 'powerSupplyBuses', [], data => (data || []).map(p => this._createNewPowerSupplyBus(p || {})));
    this.pacConsumptionBlock = Configuration.getMappedOrDefault(json, 'pacConsumptionBlock', null, data => data ? new PacConsumptionBlock(data) : null);
    this.step = Configuration.getOrDefault(json, 'step', null);
    this.slc = Configuration.getOrDefault(json, 'slc', null);
    this.starterType = Configuration.getOrDefault(json, 'starterType', '');
    this.configurationType = Configuration.getOrDefault(json, 'configurationType', '');
    this.senderEmail = Configuration.getOrDefault(json, 'senderEmail', '');
    this.senderName = Configuration.getOrDefault(json, 'senderName', '');
    this.softwareVersion = Configuration.getOrDefault(json, 'softwareVersion', '');
    this.validator = Configuration.getMappedOrDefault(json, 'validator', new Validator(), data => new Validator(data || {}));
    this.accessories = Configuration.getMappedOrDefault(json, 'accessories', null, data => new ConfigurationAccessories(data || {}));
    this.bytesUsage = Configuration.getOrDefault(json, 'bytesUsage', 0);
    this.powerSupplyManagement = Configuration.getMappedOrDefault(json, 'powerSupplyManagement', null, data => new PowerSupplyManagement(data));
    this.customOutputConsumption = Configuration.getOrDefault(json, 'customOutputConsumption', 0);
    this.softwareSelection = Configuration.getOrDefault(json, 'softwareSelection', null);
    this.softwareLicences = Configuration.getMappedOrDefault(json, 'softwareLicences', [], data => (data || []).map(l => new SoftwareLicence(l)));
    this.outdatedLicenses = Configuration.getMappedOrDefault(json, 'outdatedLicenses', [], data => (data || []).map(l => new OutdatedLicense(l)));
    this.licenseModel = Configuration.getOrDefault(json, 'licenseModel', null);
    this.softwareInfo = Configuration.getMappedOrDefault(json, 'softwareInfo', null, data => new SoftwareInfo(data));

    this.servicePlanItems = Configuration.getMappedOrDefault(json, 'servicePlanItems', [], data => (data || []).map(i => new ServicePlanItem(i)));
    this.servicePlanActivated = Configuration.getOrDefault(json, 'servicePlanActivated', true);
    this.showPrecommercializedProducts = Configuration.getOrDefault(json, 'showPrecommercializedProducts', false);
    this.seenSteps = Configuration.getMappedOrDefault(json, 'seenSteps', [], data => {
      try {
        return (data || []).map(s => s);
      } catch(e) {}
      return [];
    });
    this.country = Configuration.getOrDefault(json, 'country', '');
    this.terminalBlockPreferences = Configuration.getMappedOrDefault(json, 'terminalBlockPreferences', null, data => data ? new TerminalBlockPreferences(data) : null);
    this.hardwareToSoftwareCompatibilities = Configuration.getOrDefault(json, 'hardwareToSoftwareCompatibilities', []);
    this.softwareLicenseSelections =  Configuration.getMappedOrDefault(json, 'softwareLicenseSelections', [], data => (data || []).map(s => new SoftwareLicenseSelection(s)));
  }

  modules() {
    return [
      this.headModule,
      ...this.comModules,
      ...this.ioModules,
      ...this.localRacks.flatMap(localRackConf => localRackConf.modules()),
    ].filter(m => m != null);
  }

  /**
   * Returns the modules in the order that they are displayed from left to right.
   */
  pdfModules() {
    return [
      ...this.comModules,
      this.headModule,
      ...this.ioModules,
      ...this.localRacks.flatMap(localRackConf => localRackConf.modules()),
    ].filter(m => m != null);
  }

  /**
   * Get all modules of a configuration : current configuration modules
   * and also IO Island modules.
   */
  allModules() {
    return [
      ...this.modules(),
      ...this.ioIslands.flatMap(isl => isl.configuration.allModules()),
    ];
  }

  softwareLicenseReferences() {
    const references = [];

    if (this.softwareLicences.length > 0) {
      this.softwareLicences.forEach((softwareLicence) => {
        references.push(new LicenseItem({ id: softwareLicence.id, quantity: softwareLicence.quantity }));
      });
      if (this.servicePlanActivated) {
        this.servicePlanItems.forEach((servicePlanItem) => {
          references.push(new LicenseItem({ id: servicePlanItem.id, quantity: servicePlanItem.quantity }));
        });
      }
    }

    return references;
  }

  softwareLicenseCategories() {
    const softwareLicenseCategories = [];

    for (const softwareLicenseSelection of (this.softwareLicenseSelections || [])) {
      if (softwareLicenseSelection.softwareLicences.length > 0) {
        const references = []
        softwareLicenseSelection.softwareLicences.forEach((softwareLicence) => {
          references.push(new LicenseItem({ id: softwareLicence.id, quantity: softwareLicence.quantity }));
        });
        if (softwareLicenseSelection.servicePlanActivated) {
          softwareLicenseSelection.servicePlanItems.forEach((servicePlanItem) => {
            references.push(new LicenseItem({ id: servicePlanItem.id, quantity: servicePlanItem.quantity }));
          });
        }

        softwareLicenseCategories.push(
          { islandName: I10n.get(`screen.licence_selector.${softwareLicenseSelection.softwareSelection}.title`), islandSlot: null, references}
        );
      }
    }

    return softwareLicenseCategories;
  }

  references() {
    let references = [
      ...this.modules(),
      ...this.cartridges,
    ].flatMap(m => [
      m,
      ...m.slottedDevicesForBOM.back,
      ...m.slottedDevicesForBOM.front,
    ]).filter(m => m != null);

    if (this.rack && this.rack.id) {
      references.push(new Device({ id: this.rack.id }));
    }

    this.localRacks.forEach(localRackConf => {
      if (localRackConf.rack && localRackConf.rack.id) {
        references.push(new Device({ id: localRackConf.rack.id }));
      }
    });

    return references;
  }

  referencesForDocumentation() {
    let references = [
      ...this.modules(),
      ...this.cartridges,
    ].flatMap(m => [
      m,
      ...m.slottedDevices.back,
      ...m.slottedDevices.front,
    ]).filter(m => m != null);

    if (this.rack && this.rack.id) {
      references.push(new Device({ id: this.rack.id }));
    }

    this.softwareLicences.forEach((softwareLicence) => {
      references.push(new LicenseItem({ id: softwareLicence.id, quantity: softwareLicence.quantity }));
    });
    return references;
  }

  allReferences() {
    const references = [
      ...this.references(),
      ...this.ioIslands.flatMap(isl => isl.configuration.allReferences()),
    ];
    // Push software licenses from SLC.
    references.push(...this.softwareLicenseReferences());
    // Push hardware to software license selection.
    references.push(...this.softwareLicenseCategories().flatMap(category => category.references));

    return references;
  }

  allReferencesForDocumentation() {
    return [
      ...this.referencesForDocumentation(),
      ...this.ioIslands.flatMap(isl => isl.configuration.allReferencesForDocumentation()),
    ];
  }

  allReferencesByIsland(showSoftwareLicenses = false) {
    const result = [
      { islandName: this.name, islandSlot: 0, references: this.references() },
      ...this.ioIslands.flatMap(isl => {
        return { islandName: isl.configuration.name, islandSlot: isl.slot, references: isl.configuration.references() };
      }),
    ];

    if (showSoftwareLicenses) {
      for (const softwareLicenseCategory of this.softwareLicenseCategories()) {
        result.push(softwareLicenseCategory);
      }
    }

    return result;
  }

  /**
   * Returns the licences by group.
   */
  getSoftwareLicences() {
    return buildLicenseGroups(this.softwareLicences);
  }

  blocks() {
    return [
      this.localBlock(),
      ...this.remoteBlocks(),
    ].filter(m => m != null);
  }

  localBlock() {
    const firstIOBlock = this.ioModuleBlocks.length ? this.ioModuleBlocks[0] : this._createNewModuleBlock({});

    return this._createNewModuleBlock({
      "modules": [
        ...this.comModules.sort((a, b) => a.slot - b.slot).slice().reverse(),
        this.headModule,
        ...firstIOBlock.sortedModules(),
      ].filter(m => m != null),
    });
  }

  remoteBlocks() {
    return this.ioModuleBlocks.slice(1);
  }

  ioModuleCount() {
    return this.ioModuleBlocks
      .map(block => block.countableModules().length)
      .reduce((a, b) => a + b, 0);
  }

  comModuleCount() {
    return this.comModules.length;
  }

  nbIOConsumedCount() {
    return (this.headModule.nbIOConsumed || 0) + this.ioModules.map(m => m.nbIOConsumed || 0).reduce((a, c) => a + c, 0);
  }

  widths() {
    return this.blocks().map(b => b.width());
  }

  maxConsumptions() {
    const maxConsumptions = [];
    this.powerSupplyBuses.forEach(bus => {
      maxConsumptions.push(bus.maxConsumption);
      bus.segments.forEach(segment => {
        maxConsumptions.push(segment.maxConsumption);
      })
    });
    return maxConsumptions;
  }

  flatPowerSupplyBuses() {
    return this.powerSupplyBuses.map(bus => {
      return [
        bus,
        ...bus.segments,
      ].filter(m => m != null);
    });
  }

  /**
   * Retrieve the max module heigth for the current configuration (remote island not included)
   * @returns the max module heigth
   */
  maxModuleHeight() {
    let maxModuleHeight = 0;
    for (const mod of this.modules()) {
      const moduleHeight = mod?.height || 0;
      if (moduleHeight > maxModuleHeight) {
        maxModuleHeight = moduleHeight;
      }
    }
    if (this.rack && this.rack.height && this.rack.height > maxModuleHeight) {
      maxModuleHeight = this.rack.height;
    }

    return maxModuleHeight;
  }

  maxModuleHeightWithRemote() {
    let maxModuleHeight = 0;
    for (const mod of this.modules()) {
      const moduleHeight = mod?.height || 0;
      if (moduleHeight > maxModuleHeight) {
        maxModuleHeight = moduleHeight;
      }
    }

    if (this.rack && this.rack.height && this.rack.height > maxModuleHeight) {
      maxModuleHeight = this.rack.height;
    }

    for (const island of this.ioIslands) {
      const islandMax = island.configuration.maxModuleHeight();
      if (islandMax > maxModuleHeight) {
        maxModuleHeight = islandMax;
      }
    }
    return maxModuleHeight;
  }

  hasValidationError() {
    return this.hasMasterValidationError() || this.hasIslandsValidationError();
  }

  hasMasterValidationError() {
    return !this.validator.valid || this.modules()
      .map(device => device.hasValidationError())
      .reduce((v, v2) => v || v2, false);
  }

  hasIslandsValidationError() {
    return this.ioIslands
      .map(island => island.hasValidationError())
      .reduce((v, v2) => v || v2, false);
  }

  failedRules() {
    return [
      this.validator.failedRules,
      ...this.modules().map(device => device.validator.failedRules),
    ].flatMap(l => l);
  }

  ioGroupsAsObject() {
    const mapIOGroups = {};

    this.references().forEach((mod) => {
      const moduleIOTypesWithNumber = mod.ioTypes;
      moduleIOTypesWithNumber.forEach((ioTypeWithNumber) => {
        // If the IO group exists
        if (mapIOGroups[ioTypeWithNumber.ioType.ioGroup]) {
          // Increment the total
          mapIOGroups[ioTypeWithNumber.ioType.ioGroup].total += ioTypeWithNumber.number;
          // If the IO type exists in the IO group
          if (mapIOGroups[ioTypeWithNumber.ioType.ioGroup].ioTypesWithNumber[ioTypeWithNumber.ioType.id]) {
            mapIOGroups[ioTypeWithNumber.ioType.ioGroup].ioTypesWithNumber[ioTypeWithNumber.ioType.id].number += ioTypeWithNumber.number;
          } else {
            mapIOGroups[ioTypeWithNumber.ioType.ioGroup].ioTypesWithNumber[ioTypeWithNumber.ioType.id] =
              JSON.parse(JSON.stringify(ioTypeWithNumber));
          }
        } else {
          const ioTypes = {};
          ioTypes[ioTypeWithNumber.ioType.id] = JSON.parse(JSON.stringify(ioTypeWithNumber));
          mapIOGroups[ioTypeWithNumber.ioType.ioGroup] = {
            total: ioTypeWithNumber.number,
            ioTypesWithNumber: ioTypes,
          };
        }
      });
    });
    return mapIOGroups;
  }

  ioGroups() {
    const ioGroupsList = [];
    const mapIOGroups = this.ioGroupsAsObject();
    Object.keys(mapIOGroups).sort().forEach((ioGroupName) => {
      const ioGroup = mapIOGroups[ioGroupName];
      const listIOTypesWithNumber = [];
      Object.keys(ioGroup.ioTypesWithNumber).sort().forEach((ioTypeID) => {
        listIOTypesWithNumber.push(
          ioGroup.ioTypesWithNumber[ioTypeID]
        );
      });
      ioGroupsList.push(
        {
          groupName: ioGroupName,
          total: ioGroup.total,
          ioTypesWithNumber: listIOTypesWithNumber,
        }
      );
    });

    return ioGroupsList;
  }

  fullConfigurationIOGroups() {
    const mapIOGroups = {};
    const ioGroupsList = [];
    const configurations = [this, ...this.ioIslands.map(island => island.configuration)];

    configurations.forEach((conf) => {
      const configIoGroups = conf.ioGroupsAsObject();
      Object.keys(configIoGroups).forEach((key) => {
        const ioGroup = configIoGroups[key];
        if (mapIOGroups[key]) {
          mapIOGroups[key].total += ioGroup.total;
          Object.keys(ioGroup.ioTypesWithNumber).forEach((ioTypeKey) => {
            if (mapIOGroups[key].ioTypesWithNumber[ioTypeKey]) {
              mapIOGroups[key].ioTypesWithNumber[ioTypeKey].number += ioGroup.ioTypesWithNumber[ioTypeKey].number;
            } else {
              mapIOGroups[key].ioTypesWithNumber[ioTypeKey] = ioGroup.ioTypesWithNumber[ioTypeKey];
            }
          });
        } else {
          const ioTypes = JSON.parse(JSON.stringify(ioGroup.ioTypesWithNumber));
          mapIOGroups[key] = {
            total: ioGroup.total,
            ioTypesWithNumber: ioTypes,
          };
        }
      });
    });

    Object.keys(mapIOGroups).sort().forEach((ioGroupName) => {
      const ioGroup = mapIOGroups[ioGroupName];
      const listIOTypesWithNumber = [];
      Object.keys(ioGroup.ioTypesWithNumber).sort().forEach((ioTypeID) => {
        listIOTypesWithNumber.push(
          ioGroup.ioTypesWithNumber[ioTypeID]
        );
      });
      ioGroupsList.push(
        {
          groupName: ioGroupName,
          total: ioGroup.total,
          ioTypesWithNumber: listIOTypesWithNumber,
        }
      );

    });

    return ioGroupsList;
  }

  getIslandsConnectedWithProtocol(protocolName) {
    const concernedIslands = [];
    this.ioIslands.forEach(island => {
      if (island.protocol.name === protocolName) {
        concernedIslands.push(island);
      }

    });
    return concernedIslands;
  }

  extractReferencesByIsland(showSoftwareLicenses) {
    const allReferencesByIsland = [];
    this.allReferencesByIsland(showSoftwareLicenses).forEach((island, idx) => {
      const isFirst = idx === 0;
      const bomMap = new BOMMap();
      island.references.forEach(reference => {
        bomMap.mergeBOMMaps(reference.extractReferences());
      });

      if (isFirst) {
        this.accessories.softwareList.forEach(software => {
          bomMap.addReferencesToBOMMap(software, 1);
        });
        if (this.slc) {
          bomMap.addReferencesToBOMMap(this.slc.module.id, 1);
          for (const additionalReference of [...this.slc.module.additionalReferences]) {
            bomMap.addReferencesToBOMMap(additionalReference, 1);
          }
        }
        if (this.redboxes) {
          for (const redbox of this.redboxes) {
            bomMap.mergeBOMMaps(redbox.extractReferences());
          }
        }
        if (this.powerSupplyManagement) {
          this.powerSupplyManagement.powerSupplyGroups.forEach(group => {
            if (group.powerSupply?.id) {
              bomMap.mergeBOMMaps(group.powerSupply.extractReferences());
            }
          })
        }
      }

      island.references = bomMap.extractSortedReferences();
      allReferencesByIsland.push(island);
    });

    return allReferencesByIsland;
  }

  extractReferencesForBOM() {
    const bomMap = new BOMMap();
    this.allReferences().forEach(reference => {
      bomMap.mergeBOMMaps(reference.extractReferences());
    });
    this.accessories.softwareList.forEach(software => {
      bomMap.addReferencesToBOMMap(software, 1);
    });
    if (this.slc) {
      bomMap.addReferencesToBOMMap(this.slc.module.id, 1);
      for (const additionalReference of [...this.slc.module.additionalReferences]) {
        bomMap.addReferencesToBOMMap(additionalReference, 1);
      }
    }
    if (this.redboxes) {
      for (const redbox of this.redboxes) {
        bomMap.mergeBOMMaps(redbox.extractReferences());
      }
    }
    if (this.powerSupplyManagement) {
      this.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        if (group.powerSupply?.id) {
          bomMap.mergeBOMMaps(group.powerSupply.extractReferences());
        }
      })
    }
    return bomMap;
  }

  extractReferencesForAddToCart() {
    const bomMap = new BOMMap();
    this.allReferences()
      .filter(reference => reference.commercializationStatus !== PRECOMM_STATUS)
      .forEach(reference => {
        bomMap.mergeBOMMaps(reference.extractReferences());
      });
    this.accessories.softwareList.forEach(software => {
      bomMap.addReferencesToBOMMap(software, 1);
    });
    if (this.slc) {
      bomMap.addReferencesToBOMMap(this.slc.module.id, 1);
      for (const additionalReference of [...this.slc.module.additionalReferences]) {
        bomMap.addReferencesToBOMMap(additionalReference, 1);
      }
    }
    if (this.redboxes) {
      for (const redbox of this.redboxes) {
        bomMap.mergeBOMMaps(redbox.extractReferences());
      }
    }
    if (this.powerSupplyManagement) {
      this.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        if (group.powerSupply?.id) {
          bomMap.mergeBOMMaps(group.powerSupply.extractReferences());
        }
      })
    }
    return bomMap;
  }

  extractReferencesForDocmentation() {
    const bomMap = new BOMMap();
    this.allReferencesForDocumentation().forEach(reference => {
      bomMap.mergeBOMMaps(reference.extractReferences());
    });
    this.accessories.softwareList.forEach(software => {
      bomMap.addReferencesToBOMMap(software, 1);
    });
    if (this.slc) {
      bomMap.addReferencesToBOMMap(this.slc.module.id, 1);
      for (const additionalReference of [...this.slc.module.additionalReferences]) {
        bomMap.addReferencesToBOMMap(additionalReference, 1);
      }
    }
    if (this.redboxes) {
      for (const redbox of this.redboxes) {
        bomMap.mergeBOMMaps(redbox.extractReferences());
      }
    }
    if (this.powerSupplyManagement) {
      this.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        if (group.powerSupply?.id) {
          bomMap.mergeBOMMaps(group.powerSupply.extractReferences());
        }
      })
    }
    return bomMap;
  }

  extractDistinctReferencesForDocumentation() {
    return this.extractReferencesForDocmentation().extractDistinctReferences();
  }

  extractDistinctReferences() {
    return this.extractReferencesForBOM().extractDistinctReferences();
  }

  extractCommercializationStatusMap() {
    const result = {};
    const refs = this.references();
    for (const ref of refs) {
      if (ref && ref.id) {
        result[ref.id] = ref.commercializationStatus;
      }
    }
    return result;
  }

  findPowerSupplyBusOfModule(module) {
    let powerSupplyBus;
    let busIdx;
    for (let i = 0; i < this.powerSupplyBuses.length && !powerSupplyBus; i++) {
      if (this.powerSupplyBuses[i].containsModule(module)) {
        powerSupplyBus = this.powerSupplyBuses[i];
        busIdx = i;
      }
    }
    return [powerSupplyBus, busIdx];
  }

  findPowerSupplyBusSegmentOfModule(module) {
    let powerSupplyBusSegment;
    let nameSuffix;
    for (let i = 0; i < this.powerSupplyBuses.length && !powerSupplyBusSegment; i++) {
      const segments = (this.powerSupplyBuses[i].segments || []);
      for (let j = 0; j < segments.length && !powerSupplyBusSegment; j++) {
        if (segments[j].containsModule(module)) {
          powerSupplyBusSegment = segments[j];
          nameSuffix = `${i}${String.fromCharCode('a'.charCodeAt(0) + j)}`;
        }
      }
    }

    return [powerSupplyBusSegment, nameSuffix];
  }

  hasDeviceAccessorized = () => {
    let result = false;
    const references = this.allReferences();
    for (let i = 0; i < references.length && !result; i++) {
      const accessoriesForRef = references[i].accessoriesId() || [];
      if (accessoriesForRef.length > 0) {
        result = true;
      }
    }
    return result;
  }

  hasSoftwareLicenseSelection = () => {
    return this.softwareLicenseSelections.length > 0;
  }

  /**
   * Checks if there is any candidate module that has some candidate options.
   * Checks also if there it is possible to join softwares to the hardware configuration.
   *
   * @returns true if any accessorization is possible, false otherwise.
   */
  fullConfigurationHasDevicesToAccessorize() {
    const candidateAccessories = [];
    candidateAccessories.push(...this.headModule.candidateAccessories);
    this.ioModules.forEach(mod => {
      candidateAccessories.push(...mod.candidateAccessories);
    });
    this.comModules.forEach(mod => {
      candidateAccessories.push(...mod.candidateAccessories);
    });

    this.localRacks?.forEach(rackConfiguration => {
      candidateAccessories.push(...rackConfiguration.headModule?.candidateAccessories);
      rackConfiguration.comModules.forEach(mod => {
        candidateAccessories.push(...mod.candidateAccessories);
      });
      rackConfiguration.ioModules.forEach(mod => {
        candidateAccessories.push(...mod.candidateAccessories);
      });
    });

    this.ioIslands.forEach(island => {
      candidateAccessories.push(...island.configuration.headModule.candidateAccessories);
      island.configuration.comModules.forEach(mod => {
        candidateAccessories.push(...mod.candidateAccessories);
      });
      island.configuration.ioModules.forEach(mod => {
        candidateAccessories.push(...mod.candidateAccessories);
      });

      island.configuration?.localRacks.forEach(rackConfiguration => {
        candidateAccessories.push(...rackConfiguration.headModule?.candidateAccessories);
        rackConfiguration.comModules.forEach(mod => {
          candidateAccessories.push(...mod.candidateAccessories);
        });
        rackConfiguration.ioModules.forEach(mod => {
          candidateAccessories.push(...mod.candidateAccessories);
        });
      });

    });

    if (this.powerSupplyManagement?.powerSupplyGroups) {
      this.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        if (group.powerSupply && group.powerSupply.candidateAccessories) {
          candidateAccessories.push(...group.powerSupply.candidateAccessories);
        }
      });
    }

    return candidateAccessories.length > 0 || (this.hardwareToSoftwareCompatibilities || []).length > 0;
  }

  hasDevicesToAccessorize() {
    const candidateAccessories = [...this.headModule.candidateAccessories, ...this.ioModules.flatMap(mod => mod.candidateAccessories), ...this.comModules.flatMap(mod => mod.candidateAccessories)];

    if (this.powerSupplyManagement?.powerSupplyGroups) {
      this.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        if (group.powerSupply && group.powerSupply.candidateAccessories) {
          candidateAccessories.push(...group.powerSupply.candidateAccessories);
        }
      });
    }

    return candidateAccessories.length > 0;
  }

  extractDistinctDevicesForAccesorization() {
    let categoryIdx = 0;
    const result = [];
    const mainIslandEntry = { islandName: this.name, islandSlot: 0 };
    const moduleCategories = {};

    if (this.hasDevicesToAccessorize()) {
      if (this.headModule.candidateAccessories.length > 0) {
        moduleCategories[this.headModule.id] = { id: this.headModule.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation: null, islandName: this.name, type: configurationModuleType.head_module, recommandedAccessories: this.headModule.recommandedAccessories(), mandatoryAccessories: this.headModule.mandatoryAccessories(), modules: [{ islandSlot: 0, rackLocation: null, device: this.headModule }] };
      }
      this.ioModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
        if (mod.candidateAccessories.length > 0) {
          const entryToAdd = { islandSlot: 0, rackLocation: null, device: mod };
          if (moduleCategories[mod.id]) {
            moduleCategories[mod.id].modules.push(entryToAdd);
          } else {
            moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation: null, islandName: this.name, type: configurationModuleType.io_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
          }
        }
      });
      this.comModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
        if (mod.candidateAccessories.length > 0) {
          const entryToAdd = { islandSlot: 0, rackLocation: null, device: mod };
          if (moduleCategories[mod.id]) {
            moduleCategories[mod.id].modules.push(entryToAdd);
          } else {
            moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation: null, islandName: this.name, type: configurationModuleType.com_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
          }
        }
      });

      if (this.powerSupplyManagement) {
        this.powerSupplyManagement.powerSupplyGroups.forEach((group, index) => {
          if (group.powerSupply?.id && group.powerSupply?.candidateAccessories?.length > 0) {
            const entryToAdd = { islandSlot: 0, device: group.powerSupply };
            entryToAdd.device.slot = index;
            moduleCategories[group.powerSupply.id + ' ' + index] = { id: group.powerSupply.id, categoryIdx: categoryIdx++, islandSlot: 0, islandName: this.name, type: configurationModuleType.power_supply, recommandedAccessories: [], mandatoryAccessories: [], modules: [entryToAdd] };
          }
        })
      }

      if (this.redboxes) {
        this.redboxes.forEach((redbox, index) => {
          if(redbox?.id && redbox?.candidateAccessories?.length > 0) {
            const entryToAdd = { islandSlot: 0, rackLocation: null, device: redbox };
            entryToAdd.device.slot = index;

            if (moduleCategories[redbox.id]) {
              moduleCategories[redbox.id].modules.push(entryToAdd);
            } else {
              moduleCategories[redbox.id] = { id: redbox.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation: null, islandName: this.name, type: configurationModuleType.redbox, recommandedAccessories: redbox.recommandedAccessories(), mandatoryAccessories: redbox.mandatoryAccessories(), modules: [entryToAdd] };
            }
          }
        })
      }

      mainIslandEntry.moduleItems = moduleCategories;

      result.push(mainIslandEntry);
    }

    this.localRacks.forEach((rackConfiguration, idx) => {
      const rackLocation = (idx + 1);

      if (rackConfiguration.hasDevicesToAccessorize()) {
        const rackEntry = { islandName: `${this.name} - ${I10n.getAndReplace('saved_configuration.export.pdf.extension_rack', rackLocation)}`, islandSlot: 0, rackLocation };
        const moduleCategories = {};

        if (rackConfiguration.headModule.candidateAccessories.length > 0) {
          if (moduleCategories[rackConfiguration.headModule.id]) {
            moduleCategories[rackConfiguration.headModule.id].modules.push({ islandSlot: 0, rackLocation, device: rackConfiguration.headModule });
          } else {
            moduleCategories[rackConfiguration.headModule.id] = { id: rackConfiguration.headModule.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation, islandName: rackConfiguration.name, type: configurationModuleType.head_module, recommandedAccessories: rackConfiguration.headModule.recommandedAccessories(), mandatoryAccessories: rackConfiguration.headModule.mandatoryAccessories(), modules: [{ type: configurationModuleType.head_module, islandSlot: 0, rackLocation, device: rackConfiguration.headModule }] };
          }
        }

        rackConfiguration.ioModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
          if (mod.candidateAccessories.length > 0) {
            const entryToAdd = { islandSlot: 0, rackLocation, device: mod };
            if (moduleCategories[mod.id]) {
              moduleCategories[mod.id].modules.push(entryToAdd);
            } else {
              moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation, islandName: rackConfiguration.name, type: configurationModuleType.io_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
            }
          }
        });

        rackConfiguration.comModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
          if (mod.candidateAccessories.length > 0) {
            const entryToAdd = { islandSlot: 0, rackLocation, device: mod };
            if (moduleCategories[mod.id]) {
              moduleCategories[mod.id].modules.push(entryToAdd);
            } else {
              moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: 0, rackLocation, islandName: rackConfiguration.name, type: configurationModuleType.com_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
            }
          }
        });

        rackEntry.moduleItems = moduleCategories;

        result.push(rackEntry);
      }
    });

    this.ioIslands.forEach(island => {
      const islandConfiguration = island.configuration;

      if (islandConfiguration.hasDevicesToAccessorize()) {
        const islandEntry = { islandName: islandConfiguration.name, islandSlot: island.slot, rackLocation: null };
        const moduleCategories = {};

        if (islandConfiguration.headModule.candidateAccessories.length > 0) {
          if (moduleCategories[islandConfiguration.headModule.id]) {
            moduleCategories[islandConfiguration.headModule.id].modules.push({ islandSlot: island.slot, rackLocation: null, device: islandConfiguration.headModule });
          } else {
            moduleCategories[islandConfiguration.headModule.id] = { id: islandConfiguration.headModule.id, categoryIdx: categoryIdx++, islandSlot: island.slot, rackLocation: null, islandName: islandConfiguration.name, type: configurationModuleType.head_module, recommandedAccessories: islandConfiguration.headModule.recommandedAccessories(), mandatoryAccessories: islandConfiguration.headModule.mandatoryAccessories(), modules: [{ type: configurationModuleType.head_module, islandSlot: island.slot, device: islandConfiguration.headModule }] };
          }
        }

        islandConfiguration.ioModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
          if (mod.candidateAccessories.length > 0) {
            const entryToAdd = { islandSlot: island.slot, rackLocation: null, device: mod };
            if (moduleCategories[mod.id]) {
              moduleCategories[mod.id].modules.push(entryToAdd);
            } else {
              moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: island.slot, rackLocation: null, islandName: islandConfiguration.name, type: configurationModuleType.io_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
            }
          }
        });

        islandEntry.moduleItems = moduleCategories;

        result.push(islandEntry);
      }

      island.configuration.localRacks.forEach((rackConfiguration, idx) => {
        const rackLocation = (idx + 1);

        if (rackConfiguration.hasDevicesToAccessorize()) {
          const rackEntry = { islandName: `${islandConfiguration.name} - ${I10n.getAndReplace('saved_configuration.export.pdf.extension_rack', rackLocation)}`, islandSlot: island.slot, rackLocation };
          const moduleCategories = {};

          if (rackConfiguration.headModule.candidateAccessories.length > 0) {
            if (moduleCategories[rackConfiguration.headModule.id]) {
              moduleCategories[rackConfiguration.headModule.id].modules.push({ islandSlot: island.slot, rackLocation, device: rackConfiguration.headModule });
            } else {
              moduleCategories[rackConfiguration.headModule.id] = { id: rackConfiguration.headModule.id, categoryIdx: categoryIdx++, islandSlot: island.slot, rackLocation, islandName: rackConfiguration.name, type: configurationModuleType.head_module, recommandedAccessories: rackConfiguration.headModule.recommandedAccessories(), mandatoryAccessories: rackConfiguration.headModule.mandatoryAccessories(), modules: [{ type: configurationModuleType.head_module, islandSlot: island.slot, rackLocation, device: rackConfiguration.headModule }] };
            }
          }

          rackConfiguration.ioModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
            if (mod.candidateAccessories.length > 0) {
              const entryToAdd = { islandSlot: island.slot, rackLocation, device: mod };
              if (moduleCategories[mod.id]) {
                moduleCategories[mod.id].modules.push(entryToAdd);
              } else {
                moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: island.slot, rackLocation, islandName: rackConfiguration.name, type: configurationModuleType.io_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
              }
            }
          });

          rackConfiguration.comModules.filter(mod => mod.candidateAccessories.length > 0).forEach(mod => {
            if (mod.candidateAccessories.length > 0) {
              const entryToAdd = { islandSlot: island.slot, rackLocation, device: mod };
              if (moduleCategories[mod.id]) {
                moduleCategories[mod.id].modules.push(entryToAdd);
              } else {
                moduleCategories[mod.id] = { id: mod.id, categoryIdx: categoryIdx++, islandSlot: island.slot, rackLocation, islandName: rackConfiguration.name, type: configurationModuleType.com_module, recommandedAccessories: mod.recommandedAccessories(), mandatoryAccessories: mod.mandatoryAccessories(), modules: [entryToAdd] };
              }
            }
          });

          rackEntry.moduleItems = moduleCategories;

          result.push(rackEntry);
        }
      });

    });

    return result;
  }

  findLastM172RS485ModuleSlot() {
    const lastModule = this.ioModules.slice().reverse().find(mod => mod.isM172RS485Module());
    return lastModule ? lastModule.slot : -1;
  }

  findLastNonM172RS485ModuleSlot() {
    const lastModule = this.ioModules.slice().reverse().find(mod => !mod.isM172RS485Module() && mod.productType !== m172_battery_backup);
    return lastModule ? lastModule.slot : -1;
  }

  findLastM172SIGModuleSlot() {
    const lastModule = this.comModules.slice().reverse().find(mod => mod.productType === tm172sig_type);
    return lastModule ? lastModule.slot : -1;
  }

  hasDevicesWithRecommandedAccessories() {
    let result = false;
    const modulesToCheck = [this.headModule, ...this.ioModules, ...this.comModules];
    for (let island of this.ioIslands) {
      modulesToCheck.push(...[island.configuration.headModule, ...island.configuration.ioModules]);
    }

    for (let i = 0; i < modulesToCheck.length && !result; i++) {
      const mod = modulesToCheck[i];
      if (mod.recommandedAccessories().length > 0) {
        result = true;
      }
    }

    return result;
  }

  allDevicesHaveRecommandedAccessories() {
    let result = true;
    const modulesToCheck = [this.headModule, ...this.ioModules, ...this.comModules];

    for (let island of this.ioIslands) {
      modulesToCheck.push(...[island.configuration.headModule, ...island.configuration.ioModules]);
    }

    for (let i = 0; i < modulesToCheck.length && result; i++) {
      const mod = modulesToCheck[i];
      const moduleAccessories = mod.accessoriesId();
      const moduleRecommendedAccessories = mod.recommandedAccessories();
      if (!moduleRecommendedAccessories.every(elem => moduleAccessories.includes(elem))) {
        result = false;
      }
    }

    return result;
  }

  getInputsConsumption() {
    let result = 0;
    const modules = [...this.ioModules, ...this.comModules];
    modules.forEach(mod => {
      result += mod.inputConsumption;
    });
    return result;
  }

  getBusConsumption() {
    let consumption5v = 0;
    let consumption24v = 0;
    this.powerSupplyBuses.forEach(bus => {
      consumption5v += bus.consumption;
      bus.segments.forEach(segment => {
        consumption24v += segment.consumptionWithoutCustom;
      });
    });

    // Custom coefficient for TM3
    const customCoefficient = (this.rangeInfo.rangeIOModule === rangeIOModules.tm3 ? 0.21 : 1.0);

    return (consumption5v * customCoefficient) + consumption24v;
  }

  canUsePowerSupply() {
    let result = false;
    if (this.headModule && this.headModule.canUsePowerSupply && this.starterType !== possible_starter_selections.PAC) {
      result = true;
    }
    return result;
  }

  hasPowerSupplies() {
    let result = false;
    if (this.powerSupplyManagement && this.powerSupplyManagement.powerSupplyGroups) {
      for (let i = 0; i < this.powerSupplyManagement.powerSupplyGroups.length && !result; i++) {
        if (this.powerSupplyManagement.powerSupplyGroups[i].powerSupply != null) {
          result = true;
        }
      }
    }

    return result;

  }

  allPacPowerSuppliesFilled() {
    // Check if rack is set.
    if (this.rack && (this.rack?.id !== '') && (this.rack?.maxPowerSupplies || 0) === this.comModules.length) {
      return true;
    }

    return false;
  }

  firstOpticalFiberModule() {
    return this.ioModules.find(mod => mod.productType === pac_optical_fiber_converter);
  }

  opticalFiberConnectionSchema() {
    const result = [];
    result.push(this.firstOpticalFiberModule());
    this.ioIslands.forEach(island => {
      result.push(island.configuration.firstOpticalFiberModule());
    });

    return result;
  }

  numberOfPacIOModulesSlots() {
    let result = 0;
    result = this.rack?.maxIoModules || 0;

    // Safety coprocessor takes 2 slots
    if (this.ioModules.map(m => m.id).includes(M580_SAFETY_COPROCESSOR)) {
      result +=1;
    }

    return result;
  }

  /**
   * Checks if the configuration containes precommercialized modules.
   *
   * @returns true if any precommercialized module is detected.
   */
  containsPrecommercializedProducts() {
    return this.allReferences().some(ref => ref.commercializationStatus === PRECOMM_STATUS);
  }

  _createNewPowerSupplyBus(json) {
    const rawBus = new PowerSupplyBus(json);

    const populatedSegments = [];
    for (const segment of rawBus.segments) {
      populatedSegments.push({
        ...(segment),
        modules: this._populateModules(segment.modules),
      });
    }

    return new PowerSupplyBus({
      ...(rawBus),
      modules: this._populateModules(rawBus.modules),
      segments: populatedSegments,
    });
  }

  _createNewModuleBlock(json) {
    const rawBlock = new ModuleBlock(json);

    if (possible_starter_selections.EDGE === this.starterType) {
      return new EdgeModuleBlock({
        ...(rawBlock),
        modules: this._populateModules(rawBlock.modules),
      });
    }

    return new ModuleBlock({
      ...(rawBlock),
      modules: this._populateModules(rawBlock.modules),
    });
  }

  _populateModules(modules) {
    return (modules || []).map(module => {
      switch (module.moduleType) {
        case configurationModuleType.head_module:
          return this.headModule;
        case configurationModuleType.io_module:
          return this.ioModules.find(ioModule => ioModule.slot === module.slot);
        case configurationModuleType.com_module:
          return this.comModules.find(comModule => comModule.slot === module.slot);
        default:
      }
      return undefined;
    })
      .filter(m => !!m);
  }
}
